import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import NotificationItem from "../../Components/NotificationItem";
import { useNotifications } from "../../Contexts/NotificationProvider";
import { fetchNotifications, fetchPreferences, savePreferences } from "../../Helpers/notificationApi";
import { playChime, useSoundMuted } from "../../Helpers/ShowNotification";
import "../../Assets/css/notifications.css";
import useListParams from "../../Helpers/useListParams";

const PAGE = 20;

// The full inbox plus the person's own notification settings.
const Notifications = () => {
    const navigate = useNavigate();
    const { unread, permission, markRead, markAllRead, refreshCount, enableBrowserAlerts } = useNotifications();
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    // Unread / category filter in the query string, so Back from an opened notification keeps it - see Helpers/useListParams.
    const { filters: filter, setFilters: setFilter } = useListParams({ unread: false, category: "" });
    const [prefs, setPrefs] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [soundMuted, setSoundMuted] = useSoundMuted();

    const load = useCallback(async (reset) => {
        setLoading(true);
        const data = await fetchNotifications({ limit: PAGE, before: reset ? undefined : cursor, unread: filter.unread, category: filter.category });
        setLoading(false);
        if (!data) {
            if (reset) setItems([]);
            return;
        }
        setItems((current) => (reset ? data.items || [] : [...current, ...(data.items || [])]));
        setCursor(data.nextBefore || null);
        setHasMore(!!data.hasMore);
    }, [cursor, filter.unread, filter.category]);

    // Reload from the top whenever the filter changes.
    useEffect(() => {
        load(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter.unread, filter.category]);

    useEffect(() => {
        fetchPreferences().then((data) => { if (data) setPrefs(data); });
    }, []);

    const openItem = (item) => {
        if (!item.readAt) {
            markRead(item.notificationId);
            setItems((list) => list.map((row) => (row.notificationId === item.notificationId ? { ...row, readAt: new Date().toISOString() } : row)));
        }
        if (item.url) navigate(item.url);
    };

    const readAll = async () => {
        await markAllRead();
        setItems((list) => list.map((row) => ({ ...row, readAt: row.readAt || new Date().toISOString() })));
        refreshCount();
    };

    const setCategory = async (key, field, value) => {
        const current = prefs?.categories?.[key] || { push: true, inbox: true };
        const next = { ...current, [field]: value };
        setPrefs((state) => ({ ...state, categories: { ...(state?.categories || {}), [key]: next } }));
        const response = await savePreferences({ categories: { [key]: next } });
        if (!response?.success) toast.error("Could not save that setting");
    };

    const setQuiet = async (field, value) => {
        const current = prefs?.quietHours || { from: "22:00", to: "07:00" };
        const next = { ...current, [field]: value };
        setPrefs((state) => ({ ...state, quietHours: next }));
        if (next.from && next.to) {
            const response = await savePreferences({ quietHours: next });
            if (!response?.success) toast.error("Quiet hours must look like 22:00 to 07:00");
        }
    };

    const clearQuiet = async () => {
        setPrefs((state) => ({ ...state, quietHours: null }));
        await savePreferences({ quietHours: null });
    };

    const categories = prefs?.available || [];

    return (
        <div className="py-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div>
                    <h4 className="mb-0">Notifications</h4>
                    <div className="text-muted small">{unread ? `${unread} unread` : "You are all caught up"}</div>
                </div>
                <div className="d-flex gap-2">
                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowSettings((value) => !value)}>{showSettings ? "Hide settings" : "Settings"}</button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={readAll} disabled={!unread}>Mark all read</button>
                </div>
            </div>

            {showSettings ? (
                <div className="notif-page-card p-3 mb-3">
                    <h6 className="mb-2">Browser alerts</h6>
                    <div className="notif-settings-row">
                        <span className="small">
                            {permission === "granted" ? "On for this browser." : permission === "denied" ? "Blocked. Allow notifications in your browser's site settings." : permission === "unsupported" ? "This browser does not support alerts." : "Off for this browser."}
                        </span>
                        {permission === "default" ? <button type="button" className="btn btn-sm btn-primary" onClick={enableBrowserAlerts}>Turn on</button> : null}
                    </div>
                    <h6 className="mt-3 mb-1">Sound</h6>
                    <div className="notif-settings-row">
                        <span className="small">Play a sound for each new notification while this app is open.</span>
                        <label className="d-flex align-items-center gap-2 small"><input type="checkbox" className="form-check-input mt-0" checked={!soundMuted} onChange={(event) => { setSoundMuted(!event.target.checked); if (event.target.checked) setTimeout(playChime, 50); }} /> {soundMuted ? "Off" : "On"}</label>
                    </div>
                    <h6 className="mt-3 mb-1">What to send me</h6>
                    <div className="small text-muted mb-2">Urgent task alarms are always delivered, whatever you switch off here.</div>
                    {categories.map((category) => {
                        const state = prefs?.categories?.[category.key] || {};
                        return (
                            <div className="notif-settings-row" key={category.key}>
                                <span>{category.label}</span>
                                <span className="d-flex gap-3 small">
                                    <label className="d-flex align-items-center gap-1"><input type="checkbox" className="form-check-input mt-0" checked={state.push !== false} onChange={(event) => setCategory(category.key, "push", event.target.checked)} /> Alert</label>
                                    <label className="d-flex align-items-center gap-1"><input type="checkbox" className="form-check-input mt-0" checked={state.inbox !== false} onChange={(event) => setCategory(category.key, "inbox", event.target.checked)} /> Inbox</label>
                                </span>
                            </div>
                        );
                    })}
                    <h6 className="mt-3 mb-1">Quiet hours</h6>
                    <div className="small text-muted mb-2">Routine alerts wait until quiet hours end. Urgent ones still come through.</div>
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <input type="time" className="form-control form-control-sm w-auto" value={prefs?.quietHours?.from || ""} onChange={(event) => setQuiet("from", event.target.value)} aria-label="Quiet hours start" />
                        <span>to</span>
                        <input type="time" className="form-control form-control-sm w-auto" value={prefs?.quietHours?.to || ""} onChange={(event) => setQuiet("to", event.target.value)} aria-label="Quiet hours end" />
                        {prefs?.quietHours?.from ? <button type="button" className="btn btn-sm btn-link" onClick={clearQuiet}>Turn off</button> : null}
                    </div>
                </div>
            ) : null}

            <div className="notif-chips mb-3">
                <button type="button" className={`notif-chip${!filter.unread && !filter.category ? " is-active" : ""}`} onClick={() => setFilter({ unread: false, category: "" })}>All</button>
                <button type="button" className={`notif-chip${filter.unread ? " is-active" : ""}`} onClick={() => setFilter({ unread: true, category: "" })}>Unread</button>
                {categories.map((category) => (
                    <button type="button" key={category.key} className={`notif-chip${filter.category === category.key ? " is-active" : ""}`} onClick={() => setFilter({ unread: false, category: category.key })}>{category.label}</button>
                ))}
            </div>

            <div className="notif-page-card">
                {items.map((item) => <NotificationItem key={item.notificationId} item={item} onOpen={openItem} />)}
                {!items.length && !loading ? <div className="notif-empty">{filter.unread ? "No unread notifications." : "Nothing here yet."}</div> : null}
                {loading ? <div className="notif-empty">Loading...</div> : null}
            </div>
            {hasMore && !loading ? (
                <div className="text-center mt-3"><button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => load(false)}>Load more</button></div>
            ) : null}
        </div>
    );
};

export default Notifications;
